import { useState } from "react";
import { Text, Card } from "@merge-api/merge-javascript-shared";
import { AdminCategory, colorForAPICategory } from "../../models/Categories";

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface AggregateMetrics {
  total_fetch_blueprints: number;
  blueprints_with_at_least_one_code_block: number;
  percent_blueprints_with_at_least_one_code_block: number;
  blueprints_fully_converted: number;
  percent_blueprints_fully_converted: number;
  blueprints_with_code_block_and_no_model_writes: number;
  percent_blueprints_with_code_block_and_no_model_writes: number;
  code_block_step_ratio: number;
  code_block_step_ratio_supported_steps_only: number;
}

export interface BlueprintProgress {
  id: string;
  version_id: string;
  name: string;
  has_code_block: boolean;
  is_fully_converted: boolean;
  code_block_names: string[];
  percent_steps_code_block: number;
  percent_supported_steps_code_block: number;
  num_api_requests: number;
  num_api_request_loops: number;
  num_model_writes: number;
}

export interface BlueprintResult extends AggregateMetrics {
  blueprints: BlueprintProgress[];
}

// ─── Category color util ──────────────────────────────────────────────────────

const COLOR_TO_BAR: Record<string, string> = {
  red: "bg-red-50",
  purple: "bg-purple-50",
  green: "bg-green-50",
  gray: "bg-gray-40",
  blue: "bg-blue-50",
  orange: "bg-orange-50",
  yellow: "bg-yellow-50",
  teal: "bg-teal-50",
};

export const barClassForCategory = (category: AdminCategory): string =>
  COLOR_TO_BAR[colorForAPICategory(category) ?? "gray"] ?? "bg-gray-40";

// ─── Sort utils ───────────────────────────────────────────────────────────────

export function useSortState<T extends string>(defaultCol: T, defaultDir: "asc" | "desc" = "desc") {
  const [sortCol, setSortCol] = useState<T>(defaultCol);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(defaultDir);
  const handleSort = (col: T) => {
    if (col === sortCol) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("desc");
    }
  };
  return { sortCol, sortDir, handleSort };
}

export function SortTh<T extends string>({
  col,
  sortCol,
  sortDir,
  onSort,
  children,
}: {
  col: T;
  sortCol: T;
  sortDir: "asc" | "desc";
  onSort: (col: T) => void;
  children: React.ReactNode;
}) {
  const active = col === sortCol;
  return (
    <th scope="col" onClick={() => onSort(col)} className="cursor-pointer select-none">
      {children}
      <span className={`ml-1 text-xs ${active ? "text-gray-50" : "text-gray-20"}`}>
        {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </th>
  );
}

// ─── Shared components ────────────────────────────────────────────────────────

const StatCard = ({
  label,
  primary,
  secondary,
  percent,
  accentClass = "bg-blue-50",
}: {
  label: string;
  primary: string;
  secondary?: string;
  percent?: number;
  accentClass?: string;
}) => (
  <Card variant="outline" className="px-5 py-4 flex flex-col gap-2 border-gray-20">
    <span className="text-xs text-gray-40 uppercase tracking-widest font-semibold">{label}</span>
    <Text variant="h3" className="font-bold leading-none text-gray-70">
      {primary}
    </Text>
    {secondary && (
      <Text variant="md" className="text-gray-40">
        {secondary}
      </Text>
    )}
    {percent !== undefined && (
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 h-2 bg-gray-20 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${accentClass}`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
        <span className="text-xs text-gray-50 tabular-nums font-medium">{percent.toFixed(1)}%</span>
      </div>
    )}
  </Card>
);

export const AggregateStatCards = ({
  m,
  accentClass = "bg-blue-50",
  compact = false,
}: {
  m: AggregateMetrics;
  accentClass?: string;
  compact?: boolean;
}) => (
  <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
    <StatCard label="Total fetch blueprints" primary={String(m.total_fetch_blueprints)} />
    <StatCard
      label="With ≥1 code block"
      primary={String(m.blueprints_with_at_least_one_code_block)}
      secondary={`of ${m.total_fetch_blueprints} blueprints`}
      percent={m.percent_blueprints_with_at_least_one_code_block}
      accentClass={accentClass}
    />
    <StatCard
      label="Fully converted"
      primary={String(m.blueprints_fully_converted)}
      secondary={`of ${m.total_fetch_blueprints} blueprints`}
      percent={m.percent_blueprints_fully_converted}
      accentClass={accentClass}
    />
    {!compact && (
      <>
        <StatCard
          label="Code block, no model writes"
          primary={String(m.blueprints_with_code_block_and_no_model_writes)}
          secondary={`of ${m.total_fetch_blueprints} blueprints`}
          percent={m.percent_blueprints_with_code_block_and_no_model_writes}
          accentClass={accentClass}
        />
        <StatCard
          label="Step ratio — all steps"
          primary={`${(m.code_block_step_ratio * 100).toFixed(1)}%`}
          secondary="code block steps out of all steps"
        />
        <StatCard
          label="Step ratio — convertible steps"
          primary={`${(m.code_block_step_ratio_supported_steps_only * 100).toFixed(1)}%`}
          secondary="code block steps out of convertible steps"
        />
      </>
    )}
  </div>
);

export const ErrorBanner = ({ message }: { message: string }) => (
  <div className="rounded-md bg-red-0 border border-red-20 px-4 py-3 text-sm text-red-60 mb-6">
    {message}
  </div>
);
